import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { RiCloseCircleLine, RiFileUploadLine } from "@remixicon/react";
import { useEffect, useRef, useState } from "react";
import { useBgHover, useBorderColor } from "../../themes/colors";

interface Props {
  onFilesChange: (files: File[] | undefined) => void;
  onDeletedImg?: (id: number | undefined, image: string | undefined) => void;
  initValues?: string[];
  inputName?: string;
}

export const MultipleImageInput = ({
  onFilesChange,
  onDeletedImg,
  initValues = [],
  inputName = "images",
}: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  const bgHover = useBgHover();
  const borderColor = useBorderColor();

  useEffect(() => {
    if (initValues.length) {
      setPreviewImages(initValues);
    }
  }, [initValues]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];

    // Validate file types
    const allowedTypes = ["image/jpeg", "image/png"];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    // Validate file sizes (max 2048KB)
    const MAX_SIZE = 2048 * 1024; // 2048KB in bytes
    const oversizedFiles = validFiles.filter((file) => file.size > MAX_SIZE);

    if (oversizedFiles.length) {
      toast({
        title: "Ukuran foto melebihi batas maksimal 2MB",
        status: "error",
      });
      return;
    }

    if (validFiles.length !== files.length) {
      toast({
        title: "File tidak valid, pastikan hanya meng-upload file JPG atau PNG",
        status: "error",
      });
      return;
    }

    // Preview each file and update state
    const fileReaders = validFiles.map((file) => {
      const reader = new FileReader();
      return new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(fileReaders).then((results) => {
      setPreviewImages((prevImages) => [...prevImages, ...results]);

      // Update cumulative selected files state
      const updatedFiles = [...selectedFiles, ...validFiles];
      setSelectedFiles(updatedFiles);

      // Send the updated files array to the parent
      onFilesChange(updatedFiles);
    });
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files
      ? Array.from(event.dataTransfer.files)
      : [];

    const allowedTypes = ["image/jpeg", "image/png"];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    // Validasi ukuran file (maksimal 2048KB)
    const MAX_SIZE = 2048 * 1024; // 2048KB dalam byte
    const oversizedFiles = validFiles.filter((file) => file.size > MAX_SIZE);

    if (oversizedFiles.length) {
      toast({
        title: "Ukuran file melebihi batas maksimal 2MB.",
        status: "error",
      });
      return;
    }

    if (validFiles.length !== files.length) {
      toast({
        title: "File tidak valid, pastikan hanya meng-upload file JPG atau PNG",
        status: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImages((prevImages) => [
        ...prevImages,
        reader.result as string,
      ]); // Tambahkan gambar baru
      onFilesChange(validFiles); // Kirim file ke parent component
    };
    reader.readAsDataURL(validFiles[0]);
  };

  const removeImage = (index: number, image: string) => {
    const updatedImages = [...previewImages];
    updatedImages.splice(index, 1);

    onDeletedImg && onDeletedImg(index, image);

    setPreviewImages(updatedImages);
    !initValues &&
      onFilesChange(
        updatedImages.map((_, i) => new File([], previewImages[i]))
      ); // Kirim update file ke parent
    if (currentImage >= updatedImages.length) {
      setCurrentImage(updatedImages.length - 1); // Menyesuaikan slider agar tidak keluar dari batas
    }
  };

  const goToImage = (index: number) => {
    setCurrentImage(index);
  };

  const handleOpenInput = () => {
    previewImages.length > 5
      ? toast({ title: "Hanya bisa mengupload 6 foto", status: "error" })
      : inputRef.current?.click();
  };

  const removeAllPhotos = () => {
    setPreviewImages([]);
    onFilesChange([]);
  };

  return (
    <>
      <HStack
        w={"100%"}
        h={"60px"}
        borderColor={borderColor}
        borderWidth={"1px"}
        borderRadius={"md"}
        cursor={"pointer"}
        justify={"center"}
        _hover={{ bg: bgHover }}
        onClick={handleOpenInput}
      >
        <Icon as={RiFileUploadLine} color={"teal.400"} />

        <Text textStyle={"b-reg"}>Upload Foto Lainnya</Text>
      </HStack>

      {previewImages.length > 0 && (
        <Box
          p={4}
          border={"1px dashed"}
          borderColor={"gray.400"}
          borderRadius={"md"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {previewImages.length === 0 ? (
            <VStack fontSize={"xs"}>
              <Icon
                as={RiFileUploadLine}
                color={"teal.400"}
                fontSize={"72px"}
              />
              <Text textAlign={"center"}>
                Geser foto anda disini atau klik untuk memilih
              </Text>
              <Text color="gray.500">(JPG, JPEG, PNG)</Text>
            </VStack>
          ) : (
            <>
              <Flex
                transition="transform 0.5s ease-in-out"
                w={"100%"}
                overflowX={"hidden"}
                justifyContent="center"
                alignItems="center"
                position="relative"
              >
                <Image
                  w={"100%"}
                  maxW={"200px"} // Batas maksimal lebar slider
                  borderRadius={"md"}
                  src={previewImages[currentImage]}
                  alt={`Slide ${currentImage + 1}`}
                  objectFit={"contain"}
                  objectPosition={"center"}
                  style={{ aspectRatio: 4 / 3 }}
                />
              </Flex>

              <SimpleGrid
                w={"100%"}
                columns={6}
                gap={[1, null, 2]}
                overflowX={"auto"}
                mt={2}
              >
                {previewImages.map((image, index) => (
                  <Box
                    key={index}
                    position="relative"
                    border={currentImage === index ? "1px solid red" : "none"}
                    borderRadius={"md"}
                    overflow={"clip"}
                  >
                    <Image
                      w={"100%"}
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      objectFit="cover"
                      objectPosition={"center"}
                      cursor="pointer"
                      onClick={() => goToImage(index)}
                      style={{ aspectRatio: 1 / 1 }}
                    />

                    <Icon
                      as={RiCloseCircleLine}
                      top={1}
                      right={1}
                      cursor={"pointer"}
                      position="absolute"
                      color={"white"}
                      fontSize={[10, null, 12]}
                      onClick={() => removeImage(index, image)}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </>
          )}
        </Box>
      )}

      <Input
        ref={inputRef}
        id={inputName}
        type="file"
        name={inputName}
        accept="image/jpeg, image/png"
        onChange={handleFileChange}
        display={"none"}
        multiple={false} // Only allow single file upload at a time
      />

      {previewImages.length > 0 && (
        <Button variant="ghost" colorScheme="red" onClick={removeAllPhotos}>
          Hapus Semua Foto
        </Button>
      )}
    </>
  );
};
